/**
 * useObjectiveConfigHandlers - Centralized hook for ObjectiveConfigurationDialog handlers
 * 
 * This hook ensures consistent behavior across all components that use ObjectiveConfigurationDialog:
 * - TeamObjectiveConfiguration
 * - MemberDetailsDialog
 * - Any future components
 * 
 * CRITICAL: All objective approval, rejection, update, and allocation operations MUST go through
 * this hook to ensure collaborative sessions are properly created and validated.
 * 
 * Collaborative Session Rules:
 * 1. Approvals/Rejections require a COMPLETED collaborative session
 * 2. Updates to active objectives create a revision session (if none exists)
 * 3. Allocations create an objective_setting session (if none exists)
 * 4. Sessions are refreshed after every operation
 */

import { useState, useCallback, useEffect } from 'react'
import { format } from 'date-fns'
import { performanceService } from '@/services/api/performanceService'
import { datesForYear } from '@/lib/objectiveYear'
import toast from 'react-hot-toast'

export interface CollaborativeSession {
  id: string
  employeeId: string
  employeeName: string
  employeeRole: string
  managerId: string
  managerName: string
  sessionType: 'objective_setting' | 'objective_revision' | 'performance_discussion' | 'career_planning'
  status: 'scheduled' | 'pending-scheduling' | 'in-progress' | 'completed' | 'cancelled'
  /** Raw scheduled start (ISO). Used to treat a session as started once its time passes. */
  scheduledDateISO?: string | null
  meetingDetails?: {
    date: string
    time: string
    location: string
    meetingType: 'face-to-face' | 'virtual' | 'hybrid'
    meetingLink?: string
  }
  roleProfileConfirmed: boolean
  strategyCascadeReviewed: boolean
  objectivesDiscussed: boolean
  collaborationNotes?: string
  createdAt: string
  scheduledBy: 'manager' | 'system'
  /** Held in the year-end check-in of the year before, not booked on its own (HR UAT list #27). */
  heldWithYearEnd?: boolean
}

export interface TeamMemberBasic {
  employee_id: string
  display_name?: string
  name?: string
  job_title?: string
}

export interface UseObjectiveConfigHandlersOptions {
  managerId: string
  employeeId: string
  employeeName?: string
  employeeRole?: string
  onRefreshData?: () => Promise<void>
  /** The year whose objectives are on screen: objectives are added for it, and its meeting
   *  opens approvals. Defaults to this year. */
  year?: number
}

export interface UseObjectiveConfigHandlersReturn {
  collaborativeSessions: CollaborativeSession[]
  loadingSessions: boolean
  canApproveObjectives: boolean
  
  loadCollaborativeSessions: () => Promise<void>
  ensureCollaborativeSession: (
    sessionType?: 'objective_setting' | 'objective_revision'
  ) => Promise<CollaborativeSession | null>
  
  handleApproveObjective: (
    objectiveId: number,
    feedback?: string,
    edits?: any,
    employeeId?: string
  ) => Promise<void>
  
  handleRejectObjective: (
    objectiveId: number,
    reason: string
  ) => Promise<void>
  
  handleUpdateObjective: (
    objectiveId: number,
    updates: any,
    employeeId?: string
  ) => Promise<void>
  
  handleAllocateObjectives: (
    objectives: any[]
  ) => Promise<void>
}

/**
 * Hook that provides standardized handlers for ObjectiveConfigurationDialog
 * 
 * Usage:
 * ```tsx
 * const {
 *   collaborativeSessions,
 *   canApproveObjectives,
 *   handleApproveObjective,
 *   handleRejectObjective,
 *   handleUpdateObjective,
 *   handleAllocateObjectives
 * } = useObjectiveConfigHandlers({
 *   managerId: currentManagerId,
 *   employeeId: selectedEmployee.employee_id,
 *   employeeName: selectedEmployee.display_name,
 *   employeeRole: selectedEmployee.job_title,
 *   onRefreshData: refreshTeamData
 * })
 * ```
 */
export function useObjectiveConfigHandlers({
  managerId,
  employeeId,
  employeeName = 'Team Member',
  employeeRole = '',
  onRefreshData,
  year
}: UseObjectiveConfigHandlersOptions): UseObjectiveConfigHandlersReturn {
  const sessionYear = year ?? new Date().getFullYear()
  const [collaborativeSessions, setCollaborativeSessions] = useState<CollaborativeSession[]>([])
  const [loadingSessions, setLoadingSessions] = useState(false)

  /**
   * Load collaborative sessions for the employee
   */
  const loadCollaborativeSessions = useCallback(async () => {
    if (!employeeId || !managerId) return
    
    setLoadingSessions(true)
    try {
      const existingSessions = await performanceService.getEmployeeSessions(
        employeeId,
        { performance_year: sessionYear }
      )
      
      const transformedSessions: CollaborativeSession[] = (existingSessions || []).map((s: any) => ({
        id: s.id,
        employeeId: s.employee_id,
        employeeName: s.employee_name,
        employeeRole: s.employee_role,
        managerId: s.manager_id,
        managerName: s.manager_name,
        sessionType: s.session_type,
        status: s.status === 'pending_scheduling' ? 'pending-scheduling' :
                s.status === 'in_progress' ? 'in-progress' : s.status as any,
        scheduledDateISO: s.scheduled_date || null,
        meetingDetails: s.scheduled_date ? {
          // The date on the same clock as the time (the UTC date is still the
          // previous day until 02:00 in South Africa)
          date: format(new Date(s.scheduled_date), 'yyyy-MM-dd'),
          time: new Date(s.scheduled_date).toTimeString().slice(0, 5),
          location: s.meeting_location || '',
          meetingType: s.meeting_type === 'face_to_face' ? 'face-to-face' : s.meeting_type || 'virtual',
          meetingLink: s.meeting_link
        } : undefined,
        roleProfileConfirmed: s.role_profile_confirmed || false,
        strategyCascadeReviewed: s.strategy_cascade_reviewed || false,
        objectivesDiscussed: s.objectives_discussed || false,
        createdAt: s.created_at,
        scheduledBy: s.scheduled_by || 'system',
        heldWithYearEnd: !!s.held_with_year_end_checkin
      }))
      
      setCollaborativeSessions(transformedSessions)
      if (import.meta.env.DEV) console.log(`📋 Loaded ${transformedSessions.length} collaborative sessions for ${employeeId}`)
    } catch (error) {
      console.error('Error fetching collaborative sessions:', error)
      setCollaborativeSessions([])
    } finally {
      setLoadingSessions(false)
    }
  }, [employeeId, managerId, sessionYear])

  // Load sessions on mount and when employee changes
  useEffect(() => {
    if (employeeId && managerId) {
      loadCollaborativeSessions()
    }
  }, [employeeId, managerId, loadCollaborativeSessions])

  /**
   * Check if manager can approve/reject objectives.
   *
   * A session is treated as effectively complete the moment it starts. The backend
   * lazily flips a scheduled session to 'completed' once its start time passes, but
   * an already-open dialog may still hold the pre-flip ('scheduled') snapshot for a
   * few seconds — so we also accept a scheduled session whose start time has arrived.
   */
  const sessionHasStarted = useCallback((s: CollaborativeSession): boolean => {
    if (s.status === 'completed' || s.status === 'in-progress') return true
    if (s.status === 'scheduled' && s.scheduledDateISO) {
      return new Date(s.scheduledDateISO).getTime() <= Date.now()
    }
    return false
  }, [])

  const canApproveObjectives = useCallback((): boolean => {
    return collaborativeSessions.some(
      s => s.employeeId === employeeId && sessionHasStarted(s)
    )
  }, [collaborativeSessions, employeeId, sessionHasStarted])

  /**
   * Get detailed error message for why approval is blocked
   */
  const getApprovalBlockedReason = useCallback((): string => {
    const activeSession = collaborativeSessions.find(
      s => s.employeeId === employeeId && 
           s.status !== 'completed' && 
           s.status !== 'cancelled'
    )
    
    if (!activeSession) {
      // Check if there are ANY sessions
      const hasAnySession = collaborativeSessions.some(s => s.employeeId === employeeId)
      if (!hasAnySession) {
        return 'No collaborative session exists. Please schedule and complete a meeting first.'
      }
      return 'No active collaborative session found.'
    }
    
    if (activeSession.heldWithYearEnd) {
      return `These objectives are agreed in the ${sessionYear - 1} year-end check-in. Approvals open once that check-in starts.`
    }

    switch (activeSession.status) {
      case 'pending-scheduling':
        return 'The collaborative session has not been scheduled yet. Please schedule and complete a meeting first.'
      case 'scheduled':
        return 'Objective approvals open once the scheduled meeting time arrives.'
      case 'in-progress':
        return 'The collaborative session is in progress — you can approve objectives now.'
      default:
        return 'Objective approvals open once the scheduled meeting time arrives.'
    }
  }, [collaborativeSessions, employeeId])

  /**
   * Ensure a collaborative session exists for the employee
   * Creates one if none exists (pending_scheduling, scheduled, or in-progress)
   */
  const ensureCollaborativeSession = useCallback(async (
    sessionType: 'objective_setting' | 'objective_revision' = 'objective_revision'
  ): Promise<CollaborativeSession | null> => {
    if (!employeeId || !managerId) {
      console.warn('Cannot create session: missing employeeId or managerId')
      return null
    }
    
    try {
      // Check if there's already an active session (any type) for this employee
      const activeSession = collaborativeSessions.find(
        session => session.employeeId === employeeId && 
                   session.status !== 'completed' && 
                   session.status !== 'cancelled'
      )
      
      if (activeSession) {
        if (import.meta.env.DEV) console.log(`Active ${activeSession.sessionType} session already exists for ${employeeName}, not creating duplicate`)
        return activeSession
      }
      
      // No active session exists - create a new one
      if (import.meta.env.DEV) console.log(`Creating ${sessionType} session for ${employeeName}`)
      
      const newSession = await performanceService.createCollaborativeSession({
        employee_id: employeeId,
        employee_name: employeeName,
        employee_role: employeeRole,
        manager_id: managerId,
        manager_name: 'People Leader',
        session_type: sessionType,
        performance_year: sessionYear,
        scheduled_by: managerId
      })
      
      // Refresh sessions list
      await loadCollaborativeSessions()
      
      return newSession as CollaborativeSession
    } catch (error) {
      console.error('Error ensuring collaborative session:', error)
      return null
    }
  }, [employeeId, employeeName, employeeRole, managerId, collaborativeSessions, loadCollaborativeSessions, sessionYear])

  /**
   * Handle objective approval
   * REQUIRES a completed collaborative session
   */
  const handleApproveObjective = useCallback(async (
    objectiveId: number,
    feedback?: string,
    edits?: any,
    _employeeId?: string
  ): Promise<void> => {
    // Validate collaborative session
    if (!canApproveObjectives()) {
      const reason = getApprovalBlockedReason()
      toast.error(`Cannot approve objectives - ${reason}`, { duration: 5000 })
      throw new Error(reason)
    }
    
    try {
      await performanceService.approveIndividualObjective(objectiveId, {
        feedback,
        edits: edits ? {
          title: edits.title,
          description: edits.description,
          start_date: edits.start_date,
          end_date: edits.end_date,
          kpis: edits.kpis?.map((kpi: any) => ({
            id: kpi.id,
            name: kpi.name,
            description: kpi.description,
            target_value: kpi.target_value,
            current_value: kpi.current_value,
            unit: kpi.unit,
            frequency: kpi.frequency,
            category: kpi.category,
            status: kpi.status,
            priority: kpi.priority,
            start_date: kpi.start_date,
            end_date: kpi.end_date
          }))
        } : undefined
      })
      
      toast.success(edits ? 'Objective approved with your edits!' : 'Objective approved successfully!')
      
      // Refresh data
      await loadCollaborativeSessions()
      if (onRefreshData) await onRefreshData()
      
      if (import.meta.env.DEV) console.log('Approved objective:', objectiveId)
    } catch (error: any) {
      console.error('Error approving objective:', error)
      if (!error.message?.includes('Cannot approve')) {
        toast.error(error?.message || 'Failed to approve objective')
      }
      throw error
    }
  }, [canApproveObjectives, getApprovalBlockedReason, loadCollaborativeSessions, onRefreshData])

  /**
   * Handle objective rejection
   * REQUIRES a completed collaborative session
   */
  const handleRejectObjective = useCallback(async (
    objectiveId: number,
    reason: string
  ): Promise<void> => {
    // Validate collaborative session
    if (!canApproveObjectives()) {
      const blockedReason = getApprovalBlockedReason()
      toast.error(`Cannot reject objectives - ${blockedReason}`, { duration: 5000 })
      throw new Error(blockedReason)
    }
    
    try {
      await performanceService.rejectIndividualObjective(objectiveId, reason)
      
      toast.success('Objective rejected. Team member will be notified.')
      
      // Refresh data
      await loadCollaborativeSessions()
      if (onRefreshData) await onRefreshData()
      
      if (import.meta.env.DEV) console.log('Rejected objective:', objectiveId)
    } catch (error: any) {
      console.error('Error rejecting objective:', error)
      if (!error.message?.includes('Cannot reject')) {
        toast.error(error?.message || 'Failed to reject objective')
      }
      throw error
    }
  }, [canApproveObjectives, getApprovalBlockedReason, loadCollaborativeSessions, onRefreshData])

  /**
   * Handle objective update
   * Creates a revision session if none exists
   */
  const handleUpdateObjective = useCallback(async (
    objectiveId: number,
    updates: any,
    targetEmployeeId?: string
  ): Promise<void> => {
    const effectiveEmployeeId = targetEmployeeId || employeeId
    
    try {
      await performanceService.updateIndividualObjective(objectiveId, {
        title: updates.title,
        description: updates.description,
        start_date: updates.start_date,
        end_date: updates.end_date,
        edited_by_role: updates.edited_by_role || 'manager',
        strategic_objective_id: updates.strategic_objective_id,
        strategic_initiative_id: updates.strategic_initiative_id,
        operational_initiative_id: updates.operational_initiative_id,
        kpis: updates.kpis?.map((kpi: any) => ({
          id: kpi.id,
          name: kpi.name,
          description: kpi.description,
          target_value: kpi.target_value,
          current_value: kpi.current_value,
          unit: kpi.unit,
          frequency: kpi.frequency,
          category: kpi.category,
          status: kpi.status,
          priority: kpi.priority,
          start_date: kpi.start_date,
          end_date: kpi.end_date
        }))
      })
      
      // Ensure a collaborative session exists for discussing the changes
      const session = await ensureCollaborativeSession('objective_revision')
      if (session && session.status === 'pending-scheduling') {
        toast.success('Objective updated - please schedule a meeting to discuss changes', { duration: 4000 })
      } else {
        toast.success('Objective updated successfully!')
      }
      
      // Refresh data
      await loadCollaborativeSessions()
      if (onRefreshData) await onRefreshData()
      
      if (import.meta.env.DEV) console.log('Updated objective:', objectiveId)
    } catch (error: any) {
      console.error('Error updating objective:', error)
      toast.error(error?.message || 'Failed to update objective')
      throw error
    }
  }, [employeeId, ensureCollaborativeSession, loadCollaborativeSessions, onRefreshData])

  /**
   * Handle objective allocation
   * Creates an objective_setting session if none exists
   */
  const handleAllocateObjectives = useCallback(async (
    objectives: any[]
  ): Promise<void> => {
    if (!employeeId) {
      toast.error('No team member selected')
      throw new Error('No team member selected')
    }
    
    try {
      // Create each objective
      for (const obj of objectives) {
        const strategicObjectiveId = obj.strategicObjectiveId ?? obj.strategic_objective_id ?? null
        const strategicInitiativeId = obj.strategicInitiativeId ?? obj.strategic_initiative_id ?? null
        const operationalInitiativeId = obj.operationalInitiativeId ?? obj.operational_initiative_id ?? null
        
        // For the year on screen, dated inside it (HR UAT list #27)
        const { startDate, endDate } = datesForYear(sessionYear, obj.startDate, obj.endDate)
        const objectiveData: Record<string, any> = {
          title: obj.title,
          description: obj.description,
          kpa_id: obj.kpa || obj.kpaName,
          start_date: `${startDate}T00:00:00`,
          end_date: `${endDate}T23:59:59`,
          performance_year: sessionYear,
          created_by_manager: true,
          kpis: (obj.kpis || []).map((kpi: any) => ({
            name: kpi.title || kpi.name,
            description: kpi.description || '',
            target: kpi.target || '',
            unit: kpi.unit || '',
            frequency: kpi.frequency || 'bi-annually',
            weight: kpi.weight || 100
          }))
        }
        
        if (strategicObjectiveId) objectiveData.strategic_objective_id = strategicObjectiveId
        if (strategicInitiativeId) objectiveData.strategic_initiative_id = strategicInitiativeId
        if (operationalInitiativeId) objectiveData.operational_initiative_id = operationalInitiativeId
        
        // Through the API client: it signs the request in and uses the gateway's address (a
        // bare fetch did neither, so it only worked through the local dev proxy)
        const created = await performanceService.createEmployeeObjective(employeeId, objectiveData as any)
        if (import.meta.env.DEV) console.log(`Created objective: ${created?.objective?.title || obj.title}`)
      }
      
      // Ensure a collaborative session exists to discuss the new objectives
      const session = await ensureCollaborativeSession('objective_setting')
      
      // Refresh data
      await loadCollaborativeSessions()
      if (onRefreshData) await onRefreshData()
      
      toast.success(
        `${objectives.length} objective(s) allocated to ${employeeName}! ` +
        // An existing session comes back in this hook's shape, a new one as the API sent it
        (session?.heldWithYearEnd || (session as any)?.held_with_year_end_checkin
          ? `They are agreed in the ${sessionYear - 1} year-end check-in.`
          : `Please schedule a meeting to discuss.`),
        { duration: 5000 }
      )
      
      if (import.meta.env.DEV) console.log('Allocated objectives:', objectives.length)
    } catch (error: any) {
      console.error('Error allocating objectives:', error)
      toast.error(error.message || 'Failed to allocate objectives')
      throw error
    }
  }, [employeeId, employeeName, ensureCollaborativeSession, loadCollaborativeSessions, onRefreshData, sessionYear])

  return {
    collaborativeSessions,
    loadingSessions,
    canApproveObjectives: canApproveObjectives(),
    loadCollaborativeSessions,
    ensureCollaborativeSession,
    handleApproveObjective,
    handleRejectObjective,
    handleUpdateObjective,
    handleAllocateObjectives
  }
}

export default useObjectiveConfigHandlers
